/**
 * ==========================================================================
 *  The lighting rig in the XLMOTO tent: a mirror ball, four strobe heads and
 *  a pair of scanners, plus the light each of them puts in the air.
 *
 *  The beams are the interesting part and the reason this is not just more
 *  boxes. They are additive geometry rather than transparent geometry, which
 *  is the whole trick: additive light *adds*, so a white cone over sunlit
 *  gravel is brighter than sunlit gravel, where a transparent one would be a
 *  pale film laid over it. That is what lets a disco survive being staged
 *  outdoors on a bright morning.
 *
 *  `src/render/Disco.ts` owns the curves - when a head fires, how the ball
 *  glints, where a scanner is pointed. This end is three.js and nothing else,
 *  and it is driven by wall-clock render time: the rig is scenery, and scenery
 *  does not stop because the run has not started yet.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { DISCO } from '../config/gameplay';
import { ballAngle, ballGlint, laserSwing, strobeFlash } from './Disco';
import type { SceneAssets } from './SceneAssets';
import type { DiscoSpec, LaserSpec, StrobeSpec } from '../world/Level';

/** Straight up, for the cross products the beams are squared off with. */
const UP_AXIS = new THREE.Vector3(0, 1, 0);

/**
 * The mirror ball: what a facet is made of, and what lights it.
 *
 * Two colours rather than one because they do two jobs. `BALL_FACET` is the
 * glass itself, which is a cold grey and would stay a cold grey ball on its
 * own; `BALL_GLINT` is the emissive the strobes drive, and it is warmer than
 * the mirror because what bounces off a mirror ball is whatever is in the room
 * with it - which here is four lamps and a laptop screen, not the morning.
 */
const BALL_FACET = 0x9aa4ad;
/**
 * Warm, and deliberately well short of white. `emissiveIntensity` runs to 1 on
 * the frames a strobe fires, and a near-white emissive at 1 is a white sphere -
 * which is the one thing a mirror ball must not be, because what makes it read
 * as mirrored is the *difference* between the facets. This is bright enough to
 * catch every hit and dim enough to still be faceted while it does.
 */
const BALL_GLINT = 0x9a866a;

export class DiscoRig {
  /**
   * The rig, and the handful of things `render` moves.
   *
   * Kept as flat lists of the material and the mesh rather than looked up by
   * name each frame: this runs at render rate beside the smoke, and a scene
   * graph search per strobe head per frame is the kind of cost that is
   * invisible until it is not.
   */
  private discoGroup: THREE.Group | null = null;
  private discoBall: THREE.Mesh | null = null;
  private discoLight: THREE.PointLight | null = null;
  private readonly discoHeads: {
    spec: StrobeSpec;
    /** The head's own colour at full, which the lens is scaled off. */
    lit: THREE.Color;
    lens: THREE.MeshBasicMaterial;
    beam: THREE.Mesh;
    beamMaterial: THREE.MeshBasicMaterial;
  }[] = [];
  private readonly discoScanners: { spec: LaserSpec; swing: THREE.Object3D }[] = [];
  private strobes: readonly StrobeSpec[] = [];

  constructor(private readonly assets: SceneAssets) {}

  /** The whole crate of lights is one extension lead: it goes dark as a unit. */
  setPowerOn(on: boolean): void {
    if (this.discoGroup) this.discoGroup.visible = on;
    if (this.discoLight) this.discoLight.intensity = 0;
  }

    /**
     * The rig in the XLMOTO tent: the mirror ball, the four strobe heads and the
     * two scanners.
     *
     * The whole of it is built once and moved by `renderDisco`, which is a pure
     * function of wall-clock time - so nothing here accumulates, nothing drifts
     * while the tab is in the background, and a run that is reset does not rewind
     * the party. `src/render/Disco.ts` owns the timing; this end is three.js and
     * nothing else.
     *
     * ONE POINT LIGHT FOR FOUR HEADS. A forward renderer pays for every light on
     * every material in the level, and four flashes inside one three-metre tent
     * are one flash from anywhere a runner ever stands. So the heads' own light
     * in the air is drawn as geometry - additive cones, which is the only kind of
     * light that survives a sunlit yard - and the single point light follows
     * whichever head is currently brightest.
     */
    build(spec: DiscoSpec): THREE.Group {
      this.strobes = spec.strobes;

      const group = new THREE.Group();
      group.name = spec.name;

      // The ball. `MeshPhongMaterial` for the one object in the level that is not
      // a matte surface: a mirror ball is a specular highlight with a sphere
      // behind it, and a Lambert one is a grey ball. Flat-shaded on purpose - a
      // couple of hundred facets each catching the morning at their own angle is
      // what a mirror ball *is*, and smoothing them away is the one thing that
      // would stop it reading as one. See `DISCO.ballFacets`.
      const ball = new THREE.Mesh(
        new THREE.IcosahedronGeometry(spec.ball.radius, DISCO.ballFacets),
        new THREE.MeshPhongMaterial({
          color: BALL_FACET,
          specular: 0xffffff,
          shininess: 90,
          flatShading: true,
          emissive: BALL_GLINT,
          emissiveIntensity: DISCO.ballGlow,
        }),
      );
      ball.name = 'peilipallo';
      ball.position.set(spec.ball.center.x, spec.ball.center.y, spec.ball.center.z);
      group.add(ball);
      this.discoBall = ball;

      // And the rod it hangs off, from the top of the ball to the canopy.
      const rod = new THREE.Mesh(
        new THREE.CylinderGeometry(0.012, 0.012, spec.hanger, 6),
        this.assets.material('metal'),
      );
      rod.position.set(
        spec.ball.center.x,
        spec.ball.center.y + spec.ball.radius + spec.hanger / 2,
        spec.ball.center.z,
      );
      group.add(rod);

      for (const head of spec.strobes) {
        const lit = new THREE.Color(head.color);
        const lensMaterial = new THREE.MeshBasicMaterial({
          color: lit.clone().multiplyScalar(DISCO.lensIdle),
          fog: false,
          toneMapped: false,
        });
        const lens = new THREE.Mesh(this.assets.boxGeometry, lensMaterial);
        lens.scale.set(0.16, 0.16, 0.16);
        lens.position.set(head.lens.x, head.lens.y, head.lens.z);
        lens.name = `${head.name}-linssi`;
        group.add(lens);

        const beam = buildStrobeCone(head);
        group.add(beam);
        this.discoHeads.push({
          spec: head,
          lit,
          lens: lensMaterial,
          beam,
          beamMaterial: beam.material as THREE.MeshBasicMaterial,
        });
      }

      for (const scanner of spec.lasers) {
        // Two nested objects rather than one: the outer one carries the sweep and
        // is the only thing touched per frame, the inner one carries the fan and
        // never moves again. A single object would have to have its whole fan
        // rebuilt to be swung.
        const swing = new THREE.Object3D();
        swing.position.set(scanner.lens.x, scanner.lens.y, scanner.lens.z);
        swing.rotation.y = scanner.rotationY;
        swing.name = scanner.name;
        swing.add(buildLaserFan(scanner));
        group.add(swing);
        this.discoScanners.push({ spec: scanner, swing });
      }

      // The one light the rig actually casts. Aimed at nothing - a point light
      // has no direction - and given a distance so that what it lights is the
      // tent, the fog and the people in front of it rather than the whole yard.
      const light = new THREE.PointLight(0xffffff, 0, DISCO.lightDistance);
      light.position.set(
        spec.ball.center.x,
        spec.ball.center.y,
        spec.ball.center.z,
      );
      group.add(light);
      this.discoLight = light;

      this.discoGroup = group;
      return group;
    }

    /**
     * Render-rate update: the ball turns, the heads fire and the scanners sweep.
     *
     * Driven by wall-clock render time for the same reason `renderSmoke` is: the
     * rig is scenery, and scenery does not stop because the run has not started
     * yet. Visuals only - nothing here is read back by anything.
     */
    render(renderTime: number): void {
      if (!this.discoGroup?.visible) return;
      const strobes = this.strobes;

      if (this.discoBall) {
        this.discoBall.rotation.y = ballAngle(renderTime);
        const material = this.discoBall.material as THREE.MeshPhongMaterial;
        material.emissiveIntensity = ballGlint(strobes, renderTime);
      }

      let brightest = 0;
      for (const head of this.discoHeads) {
        const flash = strobeFlash(head.spec, renderTime);
        brightest = Math.max(brightest, flash);
        head.lens.color.copy(head.lit).multiplyScalar(DISCO.lensIdle + (1 - DISCO.lensIdle) * flash);
        // Hidden rather than drawn at zero opacity: the cones are additive and
        // double-sided, and there are four of them over one tent. What is dark
        // three quarters of the time should cost nothing three quarters of the
        // time.
        head.beam.visible = flash > 0;
        head.beamMaterial.opacity = DISCO.beamOpacity * flash;
      }
      if (this.discoLight) this.discoLight.intensity = DISCO.lightIntensity * brightest;

      for (const scanner of this.discoScanners) {
        scanner.swing.rotation.y = scanner.spec.rotationY + laserSwing(scanner.spec, renderTime);
      }
    }
}

/**
 * The light in the air in front of one strobe head: a hollow cone from the lens
 * to a disc `throwLength` away.
 *
 * Built the same way the projector's throw is - triangles from an apex to a
 * ring, additive, depth-tested but not depth-written, and shaded by vertex
 * colour rather than by a gradient nobody can afford. That construction is the
 * whole reason any of this survives a lit yard: additive light *adds*, so a
 * white cone over sunlit gravel is brighter than sunlit gravel, where a
 * transparent one would simply be a pale film.
 *
 * In world space rather than in a group of its own, because a strobe head does
 * not move: only its brightness does, and brightness is a material.
 */
function buildStrobeCone(head: StrobeSpec): THREE.Mesh {
  const dir = new THREE.Vector3(
    Math.sin(head.rotationY) * Math.cos(head.pitch),
    -Math.sin(head.pitch),
    Math.cos(head.rotationY) * Math.cos(head.pitch),
  ).normalize();

  const apex = new THREE.Vector3(head.lens.x, head.lens.y, head.lens.z);
  const centre = apex.clone().addScaledVector(dir, head.throwLength);

  // Any two axes across the beam will do - what matters is that they are
  // perpendicular to it and to each other, so the ring is a circle rather than
  // an ellipse. Straight up is the obvious first pick and the only one that
  // fails, and it fails for a head aimed at its own feet, which none is.
  const side = new THREE.Vector3().crossVectors(dir, UP_AXIS);
  if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
  side.normalize();
  const other = new THREE.Vector3().crossVectors(side, dir).normalize();

  const radius = head.spread / 2;
  const segments = 14;
  const ring: THREE.Vector3[] = [];
  for (let at = 0; at < segments; at++) {
    const angle = (at / segments) * Math.PI * 2;
    ring.push(
      centre
        .clone()
        .addScaledVector(side, Math.cos(angle) * radius)
        .addScaledVector(other, Math.sin(angle) * radius),
    );
  }

  const lit = new THREE.Color(head.color);
  const far = lit.clone().multiplyScalar(DISCO.beamFalloff);
  const positions: number[] = [];
  const colors: number[] = [];
  for (let at = 0; at < segments; at++) {
    const a = ring[at];
    const b = ring[(at + 1) % segments];
    positions.push(apex.x, apex.y, apex.z, a.x, a.y, a.z, b.x, b.y, b.z);
    colors.push(lit.r, lit.g, lit.b, far.r, far.g, far.b, far.r, far.g, far.b);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const mesh = new THREE.Mesh(geometry, beamMaterial(DISCO.beamOpacity));
  mesh.name = `${head.name}-keila`;
  // Dark until the first frame that fires it, which is most frames.
  mesh.visible = false;
  return mesh;
}

/**
 * A scanner's fan, in the frame of the object that swings it: the apex at the
 * origin, the middle of the fan down +Z, and the pitch already in the beams.
 *
 * Each beam is two quads crossed at right angles down its own axis rather than
 * a tube. A tube of six sides costs four times the triangles to say the same
 * thing, and a cross reads as round from every angle that matters because it is
 * additive - the two sheets add where they overlap, which puts a hot core down
 * the middle of the beam without anything being drawn there.
 *
 * The whole fan is one geometry. It never changes shape, only which way it is
 * pointed, and that is the parent's business.
 */
function buildLaserFan(spec: LaserSpec): THREE.Mesh {
  const positions: number[] = [];
  const colors: number[] = [];
  const lit = new THREE.Color(spec.color);
  const far = lit.clone().multiplyScalar(DISCO.laserFalloff);

  const near = DISCO.laserWidth * 0.35;
  const wide = DISCO.laserWidth * 1.4;
  const dir = new THREE.Vector3();
  const across = new THREE.Vector3();
  const other = new THREE.Vector3();
  const tip = new THREE.Vector3();

  for (let beam = 0; beam < spec.beams; beam++) {
    // Evenly across the fan, and dead centre when there is only one of them.
    const offset = spec.beams > 1 ? (beam / (spec.beams - 1) - 0.5) * spec.fan : 0;
    dir
      .set(
        Math.sin(offset) * Math.cos(spec.pitch),
        Math.sin(spec.pitch),
        Math.cos(offset) * Math.cos(spec.pitch),
      )
      .normalize();
    tip.copy(dir).multiplyScalar(spec.length);

    across.crossVectors(dir, UP_AXIS);
    if (across.lengthSq() < 1e-6) across.set(1, 0, 0);
    across.normalize();
    other.crossVectors(across, dir).normalize();

    for (const axis of [across, other]) {
      const a = axis.clone().multiplyScalar(near);
      const b = axis.clone().multiplyScalar(wide);
      // Two triangles, apex end first, so the vertex colours run down the beam
      // rather than across it.
      positions.push(
        -a.x, -a.y, -a.z, a.x, a.y, a.z, tip.x + b.x, tip.y + b.y, tip.z + b.z,
        -a.x, -a.y, -a.z, tip.x + b.x, tip.y + b.y, tip.z + b.z, tip.x - b.x, tip.y - b.y, tip.z - b.z,
      );
      colors.push(
        lit.r, lit.g, lit.b, lit.r, lit.g, lit.b, far.r, far.g, far.b,
        lit.r, lit.g, lit.b, far.r, far.g, far.b, far.r, far.g, far.b,
      );
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

  const mesh = new THREE.Mesh(geometry, beamMaterial(DISCO.laserOpacity));
  mesh.name = `${spec.name}-keilat`;
  return mesh;
}

/**
 * The one material every beam in the tent is drawn in.
 *
 * Additive because light adds; depth-tested so a speaker stack still cuts a
 * beam; not depth-written so the far side of a cone is not thrown away by the
 * near one; double-sided because you can be inside one. Unfogged and untone-
 * mapped for the same reason the projector's throw is: this is light, not a
 * surface with light on it, and the yard's haze must not eat it.
 */
function beamMaterial(opacity: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    fog: false,
    toneMapped: false,
  });
}
